<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useWebSiteStore } from '@/stores/website.store.js'

const router = useRouter()
const webSiteStore = useWebSiteStore()

const isLoading = ref(true)
const isSuccess = ref(false)
const isError = ref(false)

const goToSubscriptionsManage = () => router.push({name: 'subscriptions-manage'})
const goToSubscriptionsPurchase = () => router.push({name: 'subscriptions'})

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const verifySubscription = async(maxRetries = 5) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await webSiteStore.fetchSubscriptions()

      if (webSiteStore.hasActiveSubscription) {
        isLoading.value = false
        isSuccess.value = true
        return
      }
    } catch (error) {
      console.error('Error checking subscription:', error)
    }

    if (attempt < maxRetries) {
      await delay(2000)
    }
  }

  isLoading.value = false
  isError.value = true
}

onMounted(() => {
  verifySubscription()
})
</script>

<template>
  <div class="confirmation-container q-pa-sm flex flex-center items-center text-center column">
    <template v-if="isLoading">
      <q-spinner-dots
        color="primary"
        size="4rem"
        class="q-mb-md"
      />
      <div class="text-h5 q-mb-sm">
        {{ $t('subscriptions.verifyingPayment') }}
      </div>
      <div class="text-body1 text-grey-7">
        {{ $t('subscriptions.waiting') }}
      </div>
    </template>

    <template v-else-if="isSuccess">
      <q-icon
        name="mdi-check-circle-outline"
        color="positive"
        size="5rem"
        class="q-mb-md"
      />
      <div class="text-h4 q-mb-sm">
        {{ $t('subscriptions.confirmationTitle') }}
      </div>
      <div class="text-subtitle1 q-mb-lg">
        {{ $t('subscriptions.confirmationSubtitle') }}
      </div>
      <q-btn
        color="primary"
        @click="goToSubscriptionsManage"
      >
        {{ $t('subscriptions.goToSubscriptions') }}
      </q-btn>
    </template>

    <template v-else-if="isError">
      <q-icon
        name="mdi-alert-circle-outline"
        color="negative"
        size="5rem"
        class="q-mb-md"
      />
      <div class="text-h4 q-mb-sm text-negative">
        {{ $t('subscriptions.errorTitle') }}
      </div>
      <div class="text-subtitle1 q-mb-lg">
        {{ $t('subscriptions.errorSubtitle') }}
      </div>
      <q-btn
        color="negative"
        outline
        @click="goToSubscriptionsPurchase"
      >
        {{ $t('common.tryAgain') }}
      </q-btn>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/quasar.variables.scss';

.confirmation-container {
  min-height: 60vh;
}
</style>
